import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'
import type { Column } from '@tanstack/react-table'
import { ChevronDown, ChevronUp } from 'lucide-react'
import type React from 'react'

interface DataTableColumnHeaderProps<TData, TValue>
  extends React.ComponentProps<typeof Button> {
  column: Column<TData, TValue>
  title: string
}

export function DataTableColumnHeader<TData, TValue>({
  column,
  title,
  className,
  ...props
}: DataTableColumnHeaderProps<TData, TValue>) {
  if (!column.getCanSort()) {
    return (
      <div className={cn('text-neutral-low-contrast text-sm', className)}>
        {title}
      </div>
    )
  }

  const sorted = column.getIsSorted()

  return (
    <Button
      variant="ghost"
      onClick={() => {
        column.toggleSorting(undefined)
      }}
      className={cn(
        'group/header flex h-7 w-full items-center justify-between gap-2 px-0 py-0 text-sm font-normal',
        sorted
          ? 'text-primary-high-contrast hover:bg-transparent'
          : 'text-neutral-low-contrast hover:bg-transparent hover:text-primary-high-contrast',
        className
      )}
      {...props}>
      <span>{title}</span>
      <span
        className={cn(
          'flex flex-col transition-opacity',
          sorted ? 'opacity-100' : 'opacity-0 group-hover/header:opacity-100'
        )}>
        <ChevronUp
          className={cn(
            '-mb-0.5! size-3!',
            sorted === 'asc'
              ? 'text-primary-solid'
              : 'text-neutral-hover-border'
          )}
        />
        <ChevronDown
          className={cn(
            '-mt-0.5! size-3!',
            sorted === 'desc'
              ? 'text-primary-solid'
              : 'text-neutral-hover-border'
          )}
        />
      </span>
    </Button>
  )
}
